import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Statistic, Row, Col, Spin, message, Table } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const { Option } = Select;

interface CouponTemplate {
  id: number;
  title: string;
  issued_count: number;
  used_count: number;
}

interface CouponStats {
  title: string;
  description: string;
  discount_type: string;
  discount_value: number;
  issued_count: number;
  used_count: number;
  usage_rate: number;
  avg_days_to_use: number;
  avg_purchase_amount: number;
  total_discount_amount: number;
}

interface GenderBreakdown {
  gender: string;
  count: number;
  used_count: number;
}

interface RankBreakdown {
  rank_tier: string;
  count: number;
  used_count: number;
}

interface DailyTrend {
  use_date: string;
  count: number;
}

const CouponAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<CouponTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [stats, setStats] = useState<CouponStats | null>(null);
  const [genderBreakdown, setGenderBreakdown] = useState<GenderBreakdown[]>([]);
  const [rankBreakdown, setRankBreakdown] = useState<RankBreakdown[]>([]);
  const [dailyTrend, setDailyTrend] = useState<DailyTrend[]>([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/templates');
      if (response.data.success) {
        setTemplates(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedTemplate(response.data.data[0].id);
        }
      }
    } catch (error) {
      console.error('テンプレート取得エラー:', error);
      message.error('クーポン一覧の取得に失敗しました');
    }
  };

  const fetchAnalytics = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/by-coupon', {
        params: { template_id: selectedTemplate }
      });

      if (response.data.success) {
        setStats(response.data.data.stats);
        setGenderBreakdown(response.data.data.genderBreakdown);
        setRankBreakdown(response.data.data.rankBreakdown);
        setDailyTrend(response.data.data.dailyTrend);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('クーポン別分析エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTemplate) {
      fetchAnalytics();
    }
  }, [selectedTemplate]);

  const handleExportCSV = () => {
    if (!stats) return;

    const csvData = [{
      'クーポン名': stats.title,
      '説明': stats.description || '-',
      '割引タイプ': stats.discount_type === 'fixed' ? '固定金額' : 'パーセント',
      '割引値': stats.discount_value,
      '発行数': stats.issued_count,
      '使用数': stats.used_count,
      '使用率(%)': stats.usage_rate?.toFixed(2) || '0',
      '平均使用日数': stats.avg_days_to_use?.toFixed(1) || '-',
      '平均購入金額': stats.avg_purchase_amount ? `¥${Math.round(stats.avg_purchase_amount).toLocaleString()}` : '-',
      '総割引額': stats.total_discount_amount ? `¥${Math.round(stats.total_discount_amount).toLocaleString()}` : '-'
    }];

    downloadCSV(csvData, `クーポン別分析_${stats.title}.csv`);
    message.success('CSVファイルをダウンロードしました');
  };

  // グラフデータ
  const chartData = {
    labels: dailyTrend.map(d => d.use_date),
    datasets: [
      {
        label: '使用数',
        data: dailyTrend.map(d => d.count),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const
      },
      title: {
        display: true,
        text: '日次使用推移'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const genderColumns = [
    {
      title: '性別',
      dataIndex: 'gender',
      key: 'gender',
      render: (text: string) => {
        if (text === 'male') return '👨 男性';
        if (text === 'female') return '👩 女性';
        return text || '-';
      }
    },
    {
      title: '発行数',
      dataIndex: 'count',
      key: 'count',
      align: 'right' as const
    },
    {
      title: '使用数',
      dataIndex: 'used_count',
      key: 'used_count',
      align: 'right' as const
    },
    {
      title: '使用率',
      key: 'usage_rate',
      render: (_: any, record: GenderBreakdown) => {
        const rate = record.count > 0 ? (record.used_count / record.count * 100) : 0;
        return <span className="font-bold">{rate.toFixed(1)}%</span>;
      },
      align: 'right' as const
    }
  ];

  const rankColumns = [
    {
      title: 'ランクティア',
      dataIndex: 'rank_tier',
      key: 'rank_tier'
    },
    {
      title: '発行数',
      dataIndex: 'count',
      key: 'count',
      align: 'right' as const
    },
    {
      title: '使用数',
      dataIndex: 'used_count',
      key: 'used_count',
      align: 'right' as const
    },
    {
      title: '使用率',
      key: 'usage_rate',
      render: (_: any, record: RankBreakdown) => {
        const rate = record.count > 0 ? (record.used_count / record.count * 100) : 0;
        return <span className="font-bold">{rate.toFixed(1)}%</span>;
      },
      align: 'right' as const
    }
  ];

  return (
    <div>
      {/* クーポン選択 */}
      <Card className="mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              クーポン選択
            </label>
            <Select
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              style={{ width: '100%' }}
              placeholder="クーポンを選択してください"
            >
              {templates.map(template => (
                <Option key={template.id} value={template.id}>
                  {template.title} (発行: {template.issued_count}枚 / 使用: {template.used_count}枚)
                </Option>
              ))}
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchAnalytics}
              loading={loading}
            >
              分析
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              disabled={!stats}
            >
              CSV出力
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : stats ? (
        <>
          {/* サマリー統計 */}
          <Row gutter={16} className="mb-4">
            <Col span={6}>
              <Card>
                <Statistic
                  title="発行数"
                  value={stats.issued_count}
                  suffix="枚"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="使用数"
                  value={stats.used_count}
                  suffix="枚"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="使用率"
                  value={stats.usage_rate?.toFixed(1) || '0.0'}
                  suffix="%"
                  valueStyle={{
                    color: (stats.usage_rate || 0) >= 70 ? '#3f8600' :
                           (stats.usage_rate || 0) >= 50 ? '#faad14' : '#cf1322'
                  }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="平均使用日数"
                  value={stats.avg_days_to_use?.toFixed(1) || '-'}
                  suffix="日"
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={16} className="mb-4">
            <Col span={12}>
              <Card>
                <Statistic
                  title="平均購入金額"
                  value={stats.avg_purchase_amount ? Math.round(stats.avg_purchase_amount) : 0}
                  prefix="¥"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="総割引額"
                  value={stats.total_discount_amount ? Math.round(stats.total_discount_amount) : 0}
                  prefix="¥"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 性別・ランク別内訳 */}
          <Row gutter={16} className="mb-4">
            <Col span={12}>
              <Card title="性別内訳">
                <Table
                  columns={genderColumns}
                  dataSource={genderBreakdown}
                  rowKey="gender"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card title="ランク別内訳">
                <Table
                  columns={rankColumns}
                  dataSource={rankBreakdown}
                  rowKey="rank_tier"
                  pagination={false}
                  size="small"
                />
              </Card>
            </Col>
          </Row>

          {/* 日次推移グラフ */}
          {dailyTrend.length > 0 && (
            <Card title="日次使用推移">
              <Line data={chartData} options={chartOptions} />
            </Card>
          )}
        </>
      ) : (
        <Card>
          <div className="text-center text-gray-500 py-12">
            クーポンを選択して「分析」ボタンをクリックしてください
          </div>
        </Card>
      )}
    </div>
  );
};

export default CouponAnalytics;
